import { useEffect, useRef, useState } from 'react'
import { Button } from './Button'
import { Alert } from './Alert'

// Standard ID-1 card size (85.60mm x 53.98mm) — a Medicare card, driver's
// licence, student/staff ID, etc. are all this size.
const CARD_ASPECT_RATIO = 85.6 / 54
// A4 portrait (210mm x 297mm) — certificates, statements, bills, notices.
const PAGE_ASPECT_RATIO = 210 / 297

export type ScanShape = 'card' | 'page'

interface DocumentScannerProps {
  open: boolean
  title?: string
  shape?: ScanShape
  onCapture: (file: File) => void
  onCancel: () => void
}

// A lightweight in-browser document scanner: a live camera preview with a
// guide box shaped for the document being captured (a card-sized box for ID
// cards, an A4-shaped box for full pages), so whatever the person lines up
// inside it is captured pre-cropped and consistently framed — instead of
// handing over a raw, unedited phone photo the way a plain
// `<input type="file" capture>` does.
//
// It also auto-corrects camera roll (the phone being held slightly tilted)
// using the device's orientation sensor at the moment of capture — genuinely
// automatic, no interaction needed. This is NOT full perspective/keystone
// correction (compensating for photographing the document from an angle,
// the way CamScanner's edge-detection does) — that needs real computer
// vision (e.g. an OpenCV.js-based edge/contour detector), which is a much
// heavier dependency (~8MB of WASM) than this app currently pulls in. Roll
// correction handles the single most common cause of a "crooked" phone
// photo; alignment inside the guide box handles the rest.
export const DocumentScanner: React.FC<DocumentScannerProps> = ({ open, title = 'Scan your document', shape = 'card', onCapture, onCancel }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  // Continuously updated, read only at the instant of capture — avoids a
  // state update (and re-render) on every orientation event, which fires
  // very frequently.
  const rollRef = useRef(0)

  const [status, setStatus] = useState<'requesting' | 'streaming' | 'error'>('requesting')
  const [error, setError] = useState<string | null>(null)
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null)
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null)

  const aspectRatio = shape === 'card' ? CARD_ASPECT_RATIO : PAGE_ASPECT_RATIO
  const boxMaxWidth = shape === 'card' ? 480 : 340

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setStatus('requesting')
    setError(null)
    setCapturedUrl(null)
    setCapturedBlob(null)

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera capture is not supported in this browser')
      setStatus('error')
      return
    }

    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) videoRef.current.srcObject = stream
        setStatus('streaming')
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Could not access the camera')
        setStatus('error')
      })

    return () => {
      cancelled = true
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }, [open])

  // Device-tilt roll correction — see the component doc comment above.
  // `gamma` (left-right tilt) is a reasonable proxy for camera roll only
  // when the phone is held roughly upright (facing outward), which is how
  // people naturally hold it to photograph a card or page in front of them.
  // Bounded to ±30° and reset to 0 outside that so a nonsensical/extreme
  // sensor reading (e.g. the phone lying flat) never produces a wild
  // rotation — no correction is safer than a bad one here.
  useEffect(() => {
    if (!open) return

    const handleOrientation = (e: DeviceOrientationEvent) => {
      const gamma = e.gamma ?? 0
      rollRef.current = Math.abs(gamma) <= 30 ? gamma : 0
    }

    let cancelled = false
    const attach = () => {
      if (!cancelled) window.addEventListener('deviceorientation', handleOrientation)
    }

    const DOE = window.DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> }
    if (typeof DOE?.requestPermission === 'function') {
      // iOS 13+ gates this sensor behind an explicit permission prompt,
      // which must be triggered by a user gesture — opening the scanner
      // (a button click) counts.
      DOE.requestPermission().then((result) => {
        if (result === 'granted') attach()
      }).catch(() => {
        // Denied/unsupported — rollRef just stays 0, i.e. no correction.
      })
    } else {
      attach()
    }

    return () => {
      cancelled = true
      rollRef.current = 0
      window.removeEventListener('deviceorientation', handleOrientation)
    }
  }, [open])

  useEffect(() => {
    // Revoke the captured-frame object URL on unmount so we don't leak it —
    // separate from the effects above since those re-run per open/close.
    return () => {
      if (capturedUrl) URL.revokeObjectURL(capturedUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!open) return null

  const handleCapture = () => {
    const video = videoRef.current
    const container = containerRef.current
    const box = boxRef.current
    if (!video || !container || !box || video.videoWidth === 0) return

    const vw = video.videoWidth
    const vh = video.videoHeight
    const containerRect = container.getBoundingClientRect()
    const boxRect = box.getBoundingClientRect()

    // The video renders with object-fit: cover, so it's uniformly scaled up
    // to fill the container and center-cropped — map the guide box's
    // on-screen position back into the video's native pixel coordinates so
    // the crop lands exactly where the box appeared on screen.
    const scale = Math.max(containerRect.width / vw, containerRect.height / vh)
    const displayedW = vw * scale
    const displayedH = vh * scale
    const offsetX = (displayedW - containerRect.width) / 2
    const offsetY = (displayedH - containerRect.height) / 2

    const sx = (offsetX + (boxRect.left - containerRect.left)) / scale
    const sy = (offsetY + (boxRect.top - containerRect.top)) / scale
    const sw = boxRect.width / scale
    const sh = boxRect.height / scale

    const targetW = Math.round(sw)
    const targetH = Math.round(sh)

    const roll = rollRef.current
    if (Math.abs(roll) < 0.5) {
      // No meaningful tilt to correct — crop straight to the box.
      const canvas = document.createElement('canvas')
      canvas.width = targetW
      canvas.height = targetH
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetW, targetH)
      finishCapture(canvas)
      return
    }

    // Grab a padded region around the box, counter-rotate it by the
    // measured roll, then crop back down to the exact target size from the
    // now-level center — the padding gives the rotation room so corners
    // don't get clipped.
    const pad = 1.3
    const psw = sw * pad
    const psh = sh * pad
    const psx = sx - (psw - sw) / 2
    const psy = sy - (psh - sh) / 2

    const padded = document.createElement('canvas')
    padded.width = Math.round(psw)
    padded.height = Math.round(psh)
    const pctx = padded.getContext('2d')
    if (!pctx) return
    pctx.drawImage(video, psx, psy, psw, psh, 0, 0, padded.width, padded.height)

    const canvas = document.createElement('canvas')
    canvas.width = targetW
    canvas.height = targetH
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.translate(targetW / 2, targetH / 2)
    ctx.rotate((-roll * Math.PI) / 180)
    ctx.drawImage(padded, -padded.width / 2, -padded.height / 2)
    finishCapture(canvas)
  }

  const finishCapture = (canvas: HTMLCanvasElement) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return
        setCapturedBlob(blob)
        setCapturedUrl(URL.createObjectURL(blob))
      },
      'image/jpeg',
      0.92,
    )
  }

  const handleRetake = () => {
    if (capturedUrl) URL.revokeObjectURL(capturedUrl)
    setCapturedUrl(null)
    setCapturedBlob(null)
  }

  const handleUsePhoto = () => {
    if (!capturedBlob) return
    onCapture(new File([capturedBlob], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' }))
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 text-white shrink-0">
        <span className="text-sm font-medium">{title}</span>
        <button type="button" onClick={onCancel} className="text-sm text-white/80 hover:text-white">
          Cancel
        </button>
      </div>

      <div ref={containerRef} className="relative flex-1 overflow-hidden bg-black">
        {status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <Alert type="error">{error || 'Could not access the camera'}</Alert>
          </div>
        )}

        {status !== 'error' && !capturedUrl && (
          <>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-6">
              <div
                ref={boxRef}
                className="w-full border-2 border-white/90 rounded-lg"
                style={{ aspectRatio: `${aspectRatio}`, maxWidth: boxMaxWidth, boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)' }}
              />
            </div>
            <p className="absolute bottom-24 left-0 right-0 text-center text-xs text-white/80 px-6">
              Line the {shape === 'card' ? 'card' : 'page'} up inside the box, then capture
            </p>
          </>
        )}

        {capturedUrl && (
          <img src={capturedUrl} alt="Captured document" className="absolute inset-0 w-full h-full object-contain bg-black" />
        )}
      </div>

      <div className="p-4 flex items-center justify-center gap-4 shrink-0">
        {!capturedUrl ? (
          <button
            type="button"
            onClick={handleCapture}
            disabled={status !== 'streaming'}
            className="h-16 w-16 rounded-full bg-white border-4 border-white/40 disabled:opacity-40"
            aria-label="Capture photo"
          />
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={handleRetake}>
              Retake
            </Button>
            <Button type="button" onClick={handleUsePhoto}>
              Use Photo
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
